export const SITE_URL = 'https://metatech-official.com'
export const ORGANIZATION_ID = `${SITE_URL}/#organization`
export const WEBSITE_ID = `${SITE_URL}/#website`

const logoUrl = `${SITE_URL}/logo512.png`

export const absoluteUrl = (path = '/') => {
  if (!path) return SITE_URL
  if (/^https?:\/\//i.test(path)) return path
  const normalizedPath = path.startsWith('/') ? path : `/${path}`
  return `${SITE_URL}${normalizedPath.endsWith('/') ? normalizedPath : `${normalizedPath}/`}`
}

export const absoluteImageUrl = (imageUrl) => {
  if (!imageUrl) return logoUrl
  if (/^https?:\/\//i.test(imageUrl)) return imageUrl
  return `${SITE_URL}${imageUrl.startsWith('/') ? imageUrl : `/${imageUrl}`}`
}

export const organizationSchema = {
  '@type': 'Organization',
  '@id': ORGANIZATION_ID,
  name: 'Metatech Official',
  legalName: 'Metatech Official',
  alternateName: 'Metatech',
  url: SITE_URL,
  logo: {
    '@type': 'ImageObject',
    url: logoUrl,
    contentUrl: logoUrl,
    width: 512,
    height: 512
  },
  image: logoUrl,
  description: 'Metatech Official is a software development agency offering website development, mobile app development, SEO, design, and digital marketing services.',
  email: 'info@metatech-official.com',
  telephone: ['+92-347-2698189', '+92-336-3364624'],
  address: {
    '@type': 'PostalAddress',
    addressLocality: 'Karachi',
    addressRegion: 'Sindh',
    addressCountry: 'PK'
  },
  location: {
    '@type': 'Place',
    name: 'Metatech Official Karachi',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Karachi',
      addressRegion: 'Sindh',
      addressCountry: 'PK'
    }
  },
  areaServed: ['Pakistan', 'United States', 'United Kingdom', 'Australia', 'United Arab Emirates'],
  contactPoint: [
    {
      '@type': 'ContactPoint',
      telephone: '+92-347-2698189',
      contactType: 'sales',
      email: 'info@metatech-official.com',
      areaServed: ['PK', 'US', 'GB', 'AU', 'AE'],
      availableLanguage: ['en']
    },
    {
      '@type': 'ContactPoint',
      telephone: '+92-336-3364624',
      contactType: 'customer support',
      areaServed: ['PK', 'US', 'GB', 'AU', 'AE'],
      availableLanguage: ['en']
    }
  ],
  sameAs: [
    'https://www.facebook.com/profile.php?id=100094100574663',
    'https://twitter.com/metatech_offic',
    'https://www.instagram.com/metatech.official/',
    'https://www.linkedin.com/company/metatech-official/'
  ],
  knowsAbout: [
    'Mobile app development',
    'Website development',
    'AI app development',
    'Custom software development',
    'Search engine optimization',
    'Digital marketing',
    'UI/UX design'
  ],
  makesOffer: {
    '@type': 'OfferCatalog',
    name: 'Metatech Official Services',
    itemListElement: [
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Mobile App Development', url: absoluteUrl('/app-development') } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Website Development', url: absoluteUrl('/website-development') } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Search Engine Optimization', url: absoluteUrl('/seo') } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Google Ads PPC', url: absoluteUrl('/ppc-management-packages') } }
    ]
  }
}

export const websiteSchema = {
  '@type': 'WebSite',
  '@id': WEBSITE_ID,
  name: 'Metatech Official',
  alternateName: 'Metatech',
  url: SITE_URL,
  inLanguage: 'en',
  publisher: { '@id': ORGANIZATION_ID },
  copyrightHolder: { '@id': ORGANIZATION_ID },
  copyrightYear: 2026,
  potentialAction: {
    '@type': 'SearchAction',
    target: `${SITE_URL}/blogs/?s={search_term_string}`,
    'query-input': 'required name=search_term_string'
  }
}

export const createBreadcrumbList = (items = []) => ({
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: absoluteUrl(item.url)
  }))
})

export const createServiceItemList = ({ id, name, description, items = [] }) => ({
  '@type': 'ItemList',
  '@id': id,
  name,
  description,
  numberOfItems: items.length,
  itemListOrder: 'https://schema.org/ItemListOrderAscending',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.title || item.pageTitle,
    url: absoluteUrl(item.slug || item.path),
    item: {
      '@type': 'Service',
      '@id': `${absoluteUrl(item.slug || item.path)}#service`,
      name: item.title || item.pageTitle,
      description: item.summary,
      url: absoluteUrl(item.slug || item.path),
      provider: { '@id': ORGANIZATION_ID },
      serviceType: item.title || item.pageTitle,
      areaServed: organizationSchema.areaServed
    }
  }))
})

export const createWebPageSchema = ({ path, title, description, imageUrl, breadcrumb, mainEntity }) => ({
  '@type': 'WebPage',
  '@id': `${absoluteUrl(path)}#webpage`,
  url: absoluteUrl(path),
  name: title,
  headline: title,
  description,
  inLanguage: 'en',
  isPartOf: { '@id': WEBSITE_ID },
  publisher: { '@id': ORGANIZATION_ID },
  primaryImageOfPage: {
    '@type': 'ImageObject',
    url: absoluteImageUrl(imageUrl),
    contentUrl: absoluteImageUrl(imageUrl)
  },
  breadcrumb,
  mainEntity
})

export const createServiceSchema = ({ path, title, description, imageUrl, serviceType, relatedLinks = [] }) => ({
  '@type': 'Service',
  '@id': `${absoluteUrl(path)}#service`,
  name: title,
  serviceType: serviceType || title,
  description,
  url: absoluteUrl(path),
  image: absoluteImageUrl(imageUrl),
  provider: { '@id': ORGANIZATION_ID },
  areaServed: organizationSchema.areaServed,
  availableChannel: {
    '@type': 'ServiceChannel',
    serviceUrl: absoluteUrl('/seo-contact'),
    servicePhone: {
      '@type': 'ContactPoint',
      telephone: '+92-347-2698189',
      contactType: 'sales'
    }
  },
  hasOfferCatalog: relatedLinks.length
    ? {
        '@type': 'OfferCatalog',
        name: `${title} Related Services`,
        itemListElement: relatedLinks.map((link) => ({
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: link.title || link.pageTitle,
            url: absoluteUrl(link.slug || link.path),
            description: link.summary
          }
        }))
      }
    : undefined
})

export const createSchemaGraph = (nodes = []) => ({
  '@context': 'https://schema.org',
  '@graph': nodes.filter(Boolean)
})
